import { Stack } from '@evaneos/b2b-ui';
import cx from 'classnames';
import { FormattedDate, FormattedMessage, FormattedRelativeTime, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Rating } from '../../../design-system/Rating';
import { RepeaterBadge } from '../../../design-system/RepeaterBadge/RepeaterBadge';
import { ABMaturityLatencyExperimentChip } from '../../../shared/components/ExperimentChip';
import { MessageWithNotification } from '../../../design-system/SVG/MessageWithNotification';
import { NEW_REQUEST_STEP, DISCOVERY_STEP } from '../../../domain';
import { Avatar } from '../../../resources/agent/components/Avatar';
import { Agent } from '../../../resources/agent/types';
import { hasUnreadMessage } from '../../../resources/dossier/operations';
import { DisplayDossierOverview } from '../../../resources/dossier/types';
import { getDurationInHoursBetweenRequestDateAndToday } from '../../../resources/todo';
import { AllTranslationKeys } from '../../../shared/intl/types';
import { getToday } from '../../../shared/utilities/date';
import { HeatChip } from '../../../shared/components/DossierQualification/HeatChip';
import s from '../Todo.css';
import { useCanDisplayAvatar } from '../../../resources/agent/hooks';
import { getFeatureFlag } from '../../../shared/feature-flags/getFeatureFlag';

export type Props = {
    agent: Agent;
    value: DisplayDossierOverview;
    currentDate?: Date;
    isLate?: boolean;
};

export const DossierTodo = ({
    agent,
    value,
    currentDate = getToday(),
    isLate,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();

    const {
        id,
        project,
        rating,
        step,
        task: { definition, dueDate, note: taskNote },
        requestDate,
        requesterIsRepeater,
        isDuplicate,
        agentId,
    } = value;

    const { requesterName } = project;

    const canDisplayAvatar = useCanDisplayAvatar(agentId);
    const hasTaskNote = typeof taskNote !== 'undefined' && taskNote !== '' && taskNote !== null;
    const newRequestContactTaskDefinition = definition === 'new_request.contact';
    const isAbMaturityLatencyExperimentEnabled = getFeatureFlag('abMaturityLatencyExperiment');

    let durationInHoursBetweenRequestDateAndToday = 0;
    if (newRequestContactTaskDefinition) {
        durationInHoursBetweenRequestDateAndToday = getDurationInHoursBetweenRequestDateAndToday(
            currentDate,
            requestDate,
        );
    }
    const isRequestDateLate = durationInHoursBetweenRequestDateAndToday > 24;

    let renderTaskDueDate: JSX.Element | null;

    if (newRequestContactTaskDefinition) {
        renderTaskDueDate = (
            <p
                className={cx(s.taskDueDate, 'badge', {
                    [s.lateTask]: isRequestDateLate,
                    [s.todayTask]: !isRequestDateLate,
                })}
                data-testid={`duration-between-request-date-and-today-todo-${id}`}
            >
                <FormattedRelativeTime
                    value={-durationInHoursBetweenRequestDateAndToday}
                    unit="hour"
                    style="short"
                />
            </p>
        );
    } else if (isLate) {
        renderTaskDueDate = (
            <p
                className={cx(s.taskDueDate, s.lateTask, 'badge')}
                data-testid={`due-date-todo-${id}`}
            >
                <FormattedDate value={dueDate} day="2-digit" month="short" />
            </p>
        );
    } else {
        renderTaskDueDate = null;
    }

    return (
        <Link
            data-testid={'dossier-todo-' + id}
            to={{ pathname: `/dossier/${id}` }}
            className={cx(s.dossierTodo, {
                [s.cardDuplicate]: isDuplicate,
            })}
        >
            <div className={s.task}>
                <h3 className={s.task}>
                    <FormattedMessage id={`tasks.${definition}.name` as AllTranslationKeys} />
                </h3>
                <div className={s.step}>
                    {/* START - ABMaturityLatency */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {isAbMaturityLatencyExperimentEnabled &&
                        project.maturityExperiments?.isLatencyTest &&
                        (step === NEW_REQUEST_STEP || step === DISCOVERY_STEP) ? (
                            <ABMaturityLatencyExperimentChip size="small" />
                        ) : null}
                        {/* END - ABMaturityLatency */}
                        <p className="annotation">
                            <FormattedMessage id={`steps.${step}.name`} />
                        </p>
                    </div>
                    {step === NEW_REQUEST_STEP && <Rating value={rating} className={s.rating} />}
                </div>
            </div>
            <div className={s.project}>
                <div
                    className={cx('badge', {
                        [s.duplicate]: isDuplicate,
                    })}
                >
                    <Stack direction="row" alignItems="center">
                        {requesterName}
                        <HeatChip sx={{ ml: 1 }} heatLevel={value.heatLevel} isSelected />
                        {requesterIsRepeater && <RepeaterBadge />}
                        {isDuplicate && ' - ' + formatMessage({ id: 'dossier.duplicate.mark' })}
                    </Stack>
                </div>
                {hasTaskNote && (
                    <div className={s.taskNote} data-testid="dossier-task-note">
                        <p>{taskNote}</p>
                    </div>
                )}
            </div>
            <div className={s.dueDateAndAvatar}>
                {hasUnreadMessage(value) && (
                    <MessageWithNotification data-testid="dossier-todo-unread-message" />
                )}
                {renderTaskDueDate}
                {canDisplayAvatar && (
                    <Avatar name={agent.name} photoUrl={agent.photoUrl} className={s.avatar} />
                )}
            </div>
        </Link>
    );
};
